import React from 'react';
// import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import numeral from 'numeral';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
// import DarkUnica from 'highcharts/themes/dark-unica';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import HighchartsReact from 'highcharts-react-official';

// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

// Actions
import { gameActions } from '../../actions';

import Header from '../../components/Header';
import { DebriefMenu } from './debrief_menu';
// import infoModal from '../../assets/info-modal-1.png';

NoDataToDisplay(Highcharts);
HighchartsMore(Highcharts);
// DarkUnica(Highcharts);

const PROFILE_ORDER = [0, 1, 2];
const PROFILE_LABELS = {
  0: 'Undergraduate',
  1: 'Graduate',
  2: 'Executive Education',
};

const ALIGNMENT_LEVELS = [
  { key: 'low-alignment', label: 'Low', color: '#ca3e47' },
  { key: 'partial-alignment', label: 'Partial', color: '#e07a2d' },
  {
    key: 'moderate-high-alignment',
    label: 'Moderate-High',
    color: '#2e8f87',
  },
  { key: 'high-alignment', label: 'High', color: '#145f57' },
  { key: 'in-progress', label: 'In Progress', color: '#6b7280' },
];

const INTERVENTION_CATEGORIES = [
  {
    key: 'q1',
    label: 'Learning Objective',
    aliases: ['q1', 'learningobjective', 'learning_objective'],
  },
  {
    key: 'q2',
    label: 'Activity Design',
    aliases: ['q2', 'activitydesign', 'activity_design'],
  },
  {
    key: 'q3',
    label: 'Pedagogical Intervention',
    aliases: [
      'q3',
      'pedagogicalintervention',
      'pedagogical_intervention',
      'intervention',
    ],
  },
  {
    key: 'q4',
    label: 'Evidence Of Learning',
    aliases: ['q4', 'evidenceoflearning', 'evidence_of_learning'],
  },
];

const QUESTION_CATEGORIES = [
  { key: 'learning_objective', label: 'Q1: Learning Objective' },
  { key: 'activity_design', label: 'Q2: Activity Design' },
  { key: 'pedagogical_intervention', label: 'Q3: Pedagogical Intervention' },
  { key: 'evidence_of_learning', label: 'Q4: Evidence Of Learning' },
];

const AGREEMENT_CATEGORIES = Array.from({ length: 13 }, (_, index) => index);
const ENABLE_DARK_CHART_THEME = false;

const QUESTION_ALIGNMENT_BUCKETS = [
  { value: 0, label: 'Low' },
  { value: 1, label: 'Partial' },
  { value: 2, label: 'Moderate-High' },
  { value: 3, label: 'High' },
];

const normalizeKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const getLatestRuntimeResult = (results) => {
  if (!Array.isArray(results) || results.length === 0) return null;
  return [...results]
    .reverse()
    .find((item) => item && typeof item === 'object' && item.responses);
};

const getAlignmentLevel = (totalScore, answeredCount) => {
  if (answeredCount < 4) return 'in-progress';

  const normalizedScore = Number(totalScore) || 0;
  if (normalizedScore >= 10) return 'high-alignment';
  if (normalizedScore >= 7) return 'moderate-high-alignment';
  if (normalizedScore >= 4) return 'partial-alignment';
  return 'low-alignment';
};

const resolveInterventionIndex = (questionKey) => {
  const normalizedQuestionKey = normalizeKey(questionKey);
  return INTERVENTION_CATEGORIES.findIndex((category) =>
    category.aliases.some(
      (alias) => normalizeKey(alias) === normalizedQuestionKey,
    ),
  );
};

const getQuestionScore = (normalizedScores, aliases) => {
  const hit = aliases.find((alias) =>
    Number.isFinite(normalizedScores[normalizeKey(alias)]),
  );
  return hit ? normalizedScores[normalizeKey(hit)] : null;
};

const buildQuestionOptionTextIndex = (questions) => {
  const optionTextIndex = {};

  (questions || []).forEach((question, questionIndex) => {
    const optionMap = {};

    (question?.options || []).forEach((option) => {
      const normalizedCode = normalizeKey(option?.code);
      if (!normalizedCode) return;

      optionMap[normalizedCode] =
        option?.label || option?.decisionText || option?.text || option?.code;
    });

    if (Object.keys(optionMap).length === 0) return;

    const fallbackQuestionKey = `q${questionIndex + 1}`;
    const candidateQuestionKeys = [
      question?.key,
      question?.question_key,
      question?.code,
      question?.order ? `q${question.order}` : null,
      fallbackQuestionKey,
    ].filter(Boolean);

    candidateQuestionKeys.forEach((questionKey) => {
      optionTextIndex[normalizeKey(questionKey)] = optionMap;
    });
  });

  return optionTextIndex;
};

const getQuestionDecisionText = (
  normalizedResponses,
  aliases,
  questionOptionTextIndex,
  optionLabelByCode,
) => {
  const hit = aliases.find((alias) => normalizedResponses[normalizeKey(alias)]);
  if (!hit) return null;

  const response = normalizedResponses[normalizeKey(hit)] || {};
  const selectedCode =
    response.selectedCode || response.code || response.selectedOption;
  const normalizedSelectedCode = normalizeKey(selectedCode);

  if (normalizedSelectedCode && optionLabelByCode[normalizedSelectedCode]) {
    return optionLabelByCode[normalizedSelectedCode];
  }

  const optionMap = aliases
    .map((alias) => questionOptionTextIndex[normalizeKey(alias)])
    .find(Boolean);

  if (optionMap && selectedCode) {
    const resolvedOptionText = optionMap[normalizeKey(selectedCode)];
    if (resolvedOptionText) return resolvedOptionText;
  }

  return (
    response.decisionText ||
    response.decision ||
    response.selectedLabel ||
    response.label ||
    response.selectedCode ||
    response.code ||
    null
  );
};

const escapeHtml = (value) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const average = (values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  const valid = values.filter((value) => Number.isFinite(value));
  if (valid.length === 0) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const getQuestionAlignmentBucket = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return null;
  if (numericScore >= 3) return 3;
  if (numericScore >= 2) return 2;
  if (numericScore >= 1) return 1;
  return 0;
};

const withAlpha = (hexColor, alpha) => {
  const match = String(hexColor || '').match(
    /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i,
  );
  if (!match) return hexColor;

  const red = parseInt(match[1], 16);
  const green = parseInt(match[2], 16);
  const blue = parseInt(match[3], 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const DARK_CHART_COLORS = ['#167ede', '#ff4400', '#d30ced'];

const buildChartTheme = ({
  chart = {},
  title,
  subtitle,
  chartType,
  categories,
  xAxisOptions = {},
  yAxisTitle,
  series,
  height,
}) => {
  const isDark = ENABLE_DARK_CHART_THEME;

  return {
    chart: {
      type: chartType || 'column',
      height,
      backgroundColor: isDark ? '#0f172a' : '#ffffff',
      plotBackgroundColor: isDark ? '#0f172a' : '#ffffff',
      plotBorderColor: isDark ? '#334155' : '#d1d5db',
      style: {
        fontFamily: 'inherit',
      },
      ...chart,
    },
    colors: DARK_CHART_COLORS,
    credits: {
      enabled: false,
    },
    title: {
      text: title,
      style: {
        color: isDark ? '#f8fafc' : '#0f172a',
        fontSize: '16px',
        fontWeight: '400',
      },
    },
    subtitle: {
      text: subtitle,
      style: {
        color: isDark ? '#94a3b8' : '#475569',
      },
    },
    xAxis: {
      categories,
      crosshair: false,
      lineColor: isDark ? '#475569' : '#94a3b8',
      tickColor: isDark ? '#475569' : '#94a3b8',
      labels: {
        style: {
          color: isDark ? '#cbd5e1' : '#334155',
          fontSize: '14px',
        },
      },
      title: {
        text: null,
        style: {
          color: isDark ? '#cbd5e1' : '#334155',
        },
      },
      gridLineColor: isDark ? '#1e293b' : '#e5e7eb',
      ...xAxisOptions,
    },
    yAxis: {
      min: 0,
      allowDecimals: false,
      gridLineColor: isDark ? '#1e293b' : '#e5e7eb',
      title: {
        text: yAxisTitle,
        style: {
          color: isDark ? '#cbd5e1' : '#334155',
        },
      },
      labels: {
        style: {
          color: isDark ? '#cbd5e1' : '#334155',
        },
      },
    },
    legend: {
      style: {
        color: isDark ? '#e2e8f0' : '#334155',
        fontSize: '11px',
      },
      itemHoverStyle: {
        color: isDark ? '#ffffff' : '#0f172a',
      },
    },
    tooltip: {
      shared: true,
      backgroundColor: isDark
        ? 'rgba(15, 23, 42, 0.96)'
        : 'rgba(255, 255, 255, 0.96)',
      borderColor: isDark ? '#334155' : '#cbd5e1',
      style: {
        color: isDark ? '#f8fafc' : '#0f172a',
      },
      valueDecimals: 0,
    },
    plotOptions: {
      column: {
        borderWidth: 0,
        pointPadding: 0.08,
        groupPadding: 0.12,
        dataLabels: {
          enabled: false,
        },
      },
    },
    noData: {
      style: {
        color: isDark ? '#e2e8f0' : '#334155',
        fontSize: '12px',
      },
    },
    series,
  };
};

const buildDebriefData = (players, questions, optionLabelByCode = {}) => {
  const profileLabels = PROFILE_ORDER.map((profile) => PROFILE_LABELS[profile]);
  const questionOptionTextIndex = buildQuestionOptionTextIndex(questions);
  const alignmentByLevel = ALIGNMENT_LEVELS.reduce((acc, level) => {
    acc[level.key] = PROFILE_ORDER.map(() => 0);
    return acc;
  }, {});

  const interventionByProfile = PROFILE_ORDER.reduce((acc, profile) => {
    acc[profile] = INTERVENTION_CATEGORIES.map(() => 0);
    return acc;
  }, {});

  const preScoresByProfile = PROFILE_ORDER.reduce((acc, profile) => {
    acc[profile] = [];
    return acc;
  }, {});
  const postScoresByProfile = PROFILE_ORDER.reduce((acc, profile) => {
    acc[profile] = [];
    return acc;
  }, {});

  const questionScoresByProfile = PROFILE_ORDER.reduce((acc, profile) => {
    acc[profile] = QUESTION_CATEGORIES.map(() => []);
    return acc;
  }, {});

  const alluvialParticipants = [];

  const cumulativeAlignmentByQuestionAndProfile = PROFILE_ORDER.reduce(
    (acc, profile) => {
      acc[profile] = QUESTION_CATEGORIES.map(() => 0);
      return acc;
    },
    {},
  );

  const participantCountByProfile = PROFILE_ORDER.reduce((acc, profile) => {
    acc[profile] = 0;
    return acc;
  }, {});

  const agreementByProfile = PROFILE_ORDER.reduce((acc, profile) => {
    acc[profile] = AGREEMENT_CATEGORIES.map(() => 0);
    return acc;
  }, {});

  let totalParticipants = 0;
  let completeParticipants = 0;
  let totalSubmittedInterventions = 0;

  (players || []).forEach((player) => {
    const profileValue = Number.isFinite(Number(player?.rsub))
      ? Number(player.rsub)
      : Number(player?.rindex);
    const profilePosition = PROFILE_ORDER.findIndex(
      (profile) => profile === profileValue,
    );

    if (profilePosition === -1) return;

    totalParticipants += 1;
    participantCountByProfile[profileValue] += 1;

    const runtimeResult = getLatestRuntimeResult(player?.results);
    const responses = runtimeResult?.responses || {};
    const normalizedScores = {};
    const normalizedResponses = {};

    Object.entries(responses).forEach(([questionKey, response]) => {
      normalizedResponses[normalizeKey(questionKey)] = response;

      const score = Number(response?.score);
      if (Number.isFinite(score)) {
        normalizedScores[normalizeKey(questionKey)] = score;
      }

      const interventionIndex = resolveInterventionIndex(questionKey);
      if (
        interventionIndex > -1 &&
        response &&
        (response.selectedCode || response.code)
      ) {
        interventionByProfile[profileValue][interventionIndex] += 1;
        totalSubmittedInterventions += 1;
      }
    });

    const answeredScores = Object.values(normalizedScores).filter((score) =>
      Number.isFinite(score),
    );
    const totalScore = answeredScores.reduce((sum, score) => sum + score, 0);
    const alignmentKey = getAlignmentLevel(totalScore, answeredScores.length);
    alignmentByLevel[alignmentKey][profilePosition] += 1;
    if (answeredScores.length > 0) {
      const agreementIndex = Math.max(0, Math.min(12, Math.round(totalScore)));
      agreementByProfile[profileValue][agreementIndex] += 1;
    }

    if (answeredScores.length >= 4) completeParticipants += 1;

    const q1 = getQuestionScore(normalizedScores, ['q1', 'learning_objective']);
    const q2 = getQuestionScore(normalizedScores, ['q2', 'activity_design']);
    const q3 = getQuestionScore(normalizedScores, [
      'q3',
      'pedagogical_intervention',
      'intervention',
    ]);
    const q4 = getQuestionScore(normalizedScores, [
      'q4',
      'evidence_of_learning',
    ]);

    const d1 = getQuestionDecisionText(
      normalizedResponses,
      ['q1', 'learning_objective'],
      questionOptionTextIndex,
      optionLabelByCode,
    );
    const d2 = getQuestionDecisionText(
      normalizedResponses,
      ['q2', 'activity_design'],
      questionOptionTextIndex,
      optionLabelByCode,
    );
    const d3 = getQuestionDecisionText(
      normalizedResponses,
      ['q3', 'pedagogical_intervention', 'intervention'],
      questionOptionTextIndex,
      optionLabelByCode,
    );
    const d4 = getQuestionDecisionText(
      normalizedResponses,
      ['q4', 'evidence_of_learning'],
      questionOptionTextIndex,
      optionLabelByCode,
    );

    const preAvg = average([q1, q2]);
    const postAvg = average([q3, q4]);

    [q1, q2, q3, q4].forEach((score, index) => {
      if (Number.isFinite(score)) {
        questionScoresByProfile[profileValue][index].push(score);
      }
    });

    const questionScoresInOrder = [q1, q2, q3, q4].map((value) =>
      Number.isFinite(value) ? Number(value) : null,
    );
    const questionScoresForCumulative = questionScoresInOrder.map((value) =>
      Number.isFinite(value) ? value : 0,
    );

    let runningTotal = 0;
    questionScoresForCumulative.forEach((score, index) => {
      runningTotal += score;
      cumulativeAlignmentByQuestionAndProfile[profileValue][index] +=
        runningTotal;
    });

    if (questionScoresInOrder.some((score) => Number.isFinite(score))) {
      const path = QUESTION_CATEGORIES.map(
        (questionCategory, questionIndex) => {
          const score = questionScoresInOrder[questionIndex];
          const bucket = getQuestionAlignmentBucket(score);

          return {
            questionKey: questionCategory.key,
            questionLabel: questionCategory.label,
            score: Number.isFinite(score) ? Number(score.toFixed(2)) : null,
            alignment:
              bucket === null
                ? 'No response'
                : QUESTION_ALIGNMENT_BUCKETS[bucket].label,
            decisionText:
              [d1, d2, d3, d4][questionIndex] || 'No decision recorded',
          };
        },
      );

      alluvialParticipants.push({
        profile: profileValue,
        label:
          player?.name || player?.email || `Participant ${totalParticipants}`,
        scores: questionScoresInOrder,
        decisions: [d1, d2, d3, d4],
        path,
      });
    }

    if (Number.isFinite(preAvg)) preScoresByProfile[profileValue].push(preAvg);
    if (Number.isFinite(postAvg))
      postScoresByProfile[profileValue].push(postAvg);
  });

  const preSeriesData = PROFILE_ORDER.map((profile) => {
    const value = average(preScoresByProfile[profile]);
    return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
  });

  const postSeriesData = PROFILE_ORDER.map((profile) => {
    const value = average(postScoresByProfile[profile]);
    return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
  });

  const interventionSeries = PROFILE_ORDER.map((profile) => ({
    name: PROFILE_LABELS[profile],
    data: interventionByProfile[profile],
  }));

  const cumulativeAlignmentSeries = PROFILE_ORDER.map((profile) => ({
    name: PROFILE_LABELS[profile],
    data: cumulativeAlignmentByQuestionAndProfile[profile].map((value) => {
      const participantsInProfile = participantCountByProfile[profile];
      if (participantsInProfile === 0) return 0;
      return Number((value / participantsInProfile).toFixed(2));
    }),
  }));

  const bucketCountsByQuestion = QUESTION_CATEGORIES.map(() =>
    QUESTION_ALIGNMENT_BUCKETS.map(() => 0),
  );

  alluvialParticipants.forEach((participant) => {
    participant.scores.forEach((score, questionIndex) => {
      const bucket = getQuestionAlignmentBucket(score);
      if (bucket === null) return;
      bucketCountsByQuestion[questionIndex][bucket] += 1;
    });
  });

  const maxBucketOccupancy = bucketCountsByQuestion.reduce(
    (maxCount, counts) => {
      const localMax = Math.max(...counts);
      return Math.max(maxCount, localMax);
    },
    0,
  );

  const laneSpacing =
    maxBucketOccupancy > 0 ? Math.min(0.24, 0.84 / maxBucketOccupancy) : 0.16;

  const laneUsageByQuestion = QUESTION_CATEGORIES.map(() =>
    QUESTION_ALIGNMENT_BUCKETS.map(() => 0),
  );

  const alignmentAlluvialSeries = PROFILE_ORDER.flatMap((profile) => {
    const participantsInProfile = alluvialParticipants.filter(
      (participant) => participant.profile === profile,
    );

    const profileSeriesId = `alignment-profile-${profile}`;
    const profileColor =
      DARK_CHART_COLORS[profile % DARK_CHART_COLORS.length] ||
      DARK_CHART_COLORS[0];

    const profileLegendSeries = {
      id: profileSeriesId,
      name: PROFILE_LABELS[profile],
      type: 'spline',
      data: [],
      showInLegend: true,
      enableMouseTracking: false,
      lineWidth: 0,
      marker: {
        enabled: true,
        radius: 6,
      },
      color: withAlpha(profileColor, 0.8),
    };

    const participantSeries = participantsInProfile.map((participant) => {
      const data = QUESTION_CATEGORIES.map((_, questionIndex) => {
        const score = participant.scores?.[questionIndex] ?? null;
        const decisionText = participant.decisions?.[questionIndex] || null;
        const bucket = getQuestionAlignmentBucket(score);
        if (bucket === null) {
          return {
            x: questionIndex,
            y: null,
            custom: {
              score: null,
              bucketLabel: 'No response',
              participantLabel: participant.label,
              decisionText,
              participantPath: participant.path || [],
              seriesProfile: PROFILE_LABELS[profile],
            },
          };
        }

        const laneIndex = laneUsageByQuestion[questionIndex][bucket];
        laneUsageByQuestion[questionIndex][bucket] += 1;

        const bucketCount = bucketCountsByQuestion[questionIndex][bucket];
        const laneOffset = (laneIndex - (bucketCount - 1) / 2) * laneSpacing;
        const yValue = Number((bucket + laneOffset).toFixed(3));

        return {
          x: questionIndex,
          y: yValue,
          custom: {
            score: Number(score.toFixed(2)),
            bucketLabel: QUESTION_ALIGNMENT_BUCKETS[bucket].label,
            participantLabel: participant.label,
            decisionText,
            participantPath: participant.path || [],
            seriesProfile: PROFILE_LABELS[profile],
          },
        };
      });

      return {
        linkedTo: profileSeriesId,
        name: PROFILE_LABELS[profile],
        color: withAlpha(profileColor, 0.8),
        type: 'spline',
        lineWidth: 4,
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 10,
          },
          inactive: {
            opacity: 1,
          },
        },
        showInLegend: false,
        connectNulls: false,
        data,
      };
    });

    return [profileLegendSeries, ...participantSeries];
  });

  const questionAlignmentSeries = PROFILE_ORDER.map((profile) => ({
    name: PROFILE_LABELS[profile],
    data: QUESTION_CATEGORIES.map((_, index) =>
      Number(
        (average(questionScoresByProfile[profile][index]) || 0).toFixed(2),
      ),
    ),
  }));

  const agreementSeries = PROFILE_ORDER.map((profile) => ({
    name: PROFILE_LABELS[profile],
    data: agreementByProfile[profile],
  }));

  const completionRate =
    totalParticipants > 0
      ? Number(((completeParticipants / totalParticipants) * 100).toFixed(1))
      : 0;

  return {
    profileLabels,
    alignmentSeries: ALIGNMENT_LEVELS.map((level) => ({
      name: level.label,
      color: level.color,
      data: alignmentByLevel[level.key],
    })),
    interventionCategories: INTERVENTION_CATEGORIES.map(
      (category) => category.label,
    ),
    questionCategories: QUESTION_CATEGORIES.map((question) => question.label),
    agreementCategories: AGREEMENT_CATEGORIES,
    interventionSeries,
    cumulativeAlignmentSeries,
    questionAlignmentSeries,
    agreementSeries,
    alignmentAlluvialSeries,
    preSeriesData,
    postSeriesData,
    completionRate,
    totalParticipants,
    completeParticipants,
    totalSubmittedInterventions,
    hasPrePostData:
      preSeriesData.some((value) => Number.isFinite(value)) ||
      postSeriesData.some((value) => Number.isFinite(value)),
  };
};

class DebriefSummary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      optionLabelByCode: {},
      selectedParticipantPath: null,
    };

    this.fetchDecisionOptionLabels = this.fetchDecisionOptionLabels.bind(this);
    this.handleParticipantPointClick =
      this.handleParticipantPointClick.bind(this);
    this.closeParticipantPathModal = this.closeParticipantPathModal.bind(this);
  }

  componentDidMount() {
    const { dispatch, match, game } = this.props;
    if (match.params.id) {
      dispatch(gameActions.fetchGames(match.params.id));
    }

    if (match.params.id) {
      this.fetchDecisionOptionLabels(match.params.id);
    }
  }

  fetchDecisionOptionLabels(gameId) {
    fetch(`${process.env.REACT_APP_API_URL}/games/${gameId}/decision-options`)
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((res) => {
        if (!res || typeof res !== 'object') return;
        this.setState({ optionLabelByCode: res.optionLabelByCode || {} });
      })
      .catch((err) => {
        console.log('Failed loading decision option labels', err);
      });
  }

  handleParticipantPointClick(pointCustom) {
    if (!pointCustom || !Array.isArray(pointCustom.participantPath)) return;

    this.setState({
      selectedParticipantPath: {
        participantLabel: pointCustom.participantLabel || 'Participant',
        path: pointCustom.participantPath,
        bucket: pointCustom.bucketLabel || 'No response',
        profile: pointCustom.seriesProfile || null,
      },
    });
  }

  closeParticipantPathModal() {
    this.setState({ selectedParticipantPath: null });
  }

  render() {
    const { game } = this.props;
    const { optionLabelByCode, selectedParticipantPath } = this.state;
    const onParticipantPointClick = (pointCustom) =>
      this.handleParticipantPointClick(pointCustom);
    const gameData = game?.data || {};
    const players = Array.isArray(gameData?.players) ? gameData.players : [];
    const questions = Array.isArray(gameData?.gameplay?.questions)
      ? gameData.gameplay.questions
      : [];

    const {
      profileLabels,
      alignmentSeries,
      interventionCategories,
      interventionSeries,
      questionCategories,
      cumulativeAlignmentSeries,
      questionAlignmentSeries,
      agreementCategories,
      agreementSeries,
      alignmentAlluvialSeries,
      preSeriesData,
      postSeriesData,
      completionRate,
      totalParticipants,
      completeParticipants,
      totalSubmittedInterventions,
      hasPrePostData,
    } = buildDebriefData(players, questions, optionLabelByCode);

    const cumulativeAlignmentHistogramOptions = {
      ...buildChartTheme({
        chartType: 'scatter',
        title: 'Cumulative Alignment By Question And Academic Profile',
        subtitle:
          totalSubmittedInterventions === 0
            ? 'No scored responses found yet for Q1-Q4.'
            : '',
        categories: questionCategories,
        yAxisTitle: 'Cumulative Alignment Level',
        series: cumulativeAlignmentSeries,
        height: 300,
      }),
    };

    const questionAlignmentRadarOptions = {
      chart: {
        polar: true,
        // type: 'line',
        height: 360,
        backgroundColor: ENABLE_DARK_CHART_THEME ? '#0f172a' : '#ffffff',
        plotBackgroundColor: ENABLE_DARK_CHART_THEME ? '#0f172a' : '#ffffff',
      },
      colors: DARK_CHART_COLORS,
      credits: {
        enabled: false,
      },
      title: {
        text: 'Alignment Level By Question',
        style: {
          color: ENABLE_DARK_CHART_THEME ? '#f8fafc' : '#0f172a',
          fontSize: '14px',
          fontWeight: '600',
        },
      },
      subtitle: {
        text:
          totalParticipants === 0
            ? 'No participant records available for this session.'
            : '',
        style: {
          color: ENABLE_DARK_CHART_THEME ? '#94a3b8' : '#475569',
        },
      },
      pane: {
        size: '78%',
        background: {
          backgroundColor: ENABLE_DARK_CHART_THEME ? '#0f172a' : '#ffffff',
          borderColor: ENABLE_DARK_CHART_THEME ? '#334155' : '#d1d5db',
        },
      },
      xAxis: {
        min: 0,
        max: 3,
        categories: questionCategories,
        tickmarkPlacement: 'on',
        lineWidth: 0,
        labels: {
          style: {
            color: ENABLE_DARK_CHART_THEME ? '#cbd5e1' : '#334155',
          },
        },
      },
      yAxis: {
        gridLineInterpolation: 'polygon',
        min: 0,
        max: 3,
        tickInterval: 0.5,
        lineWidth: 0,
        labels: {
          style: {
            color: ENABLE_DARK_CHART_THEME ? '#cbd5e1' : '#334155',
          },
        },
        title: {
          text: 'Average alignment score',
          style: {
            color: ENABLE_DARK_CHART_THEME ? '#cbd5e1' : '#334155',
          },
        },
      },
      legend: {
        itemStyle: {
          color: ENABLE_DARK_CHART_THEME ? '#e2e8f0' : '#334155',
          fontSize: '11px',
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: ENABLE_DARK_CHART_THEME
          ? 'rgba(15, 23, 42, 0.96)'
          : 'rgba(255, 255, 255, 0.96)',
        borderColor: ENABLE_DARK_CHART_THEME ? '#334155' : '#cbd5e1',
        style: {
          color: ENABLE_DARK_CHART_THEME ? '#f8fafc' : '#0f172a',
        },
        valueDecimals: 2,
      },
      plotOptions: {
        series: {
          marker: {
            enabled: true,
            radius: 3,
          },
        },
      },
      noData: {
        style: {
          color: ENABLE_DARK_CHART_THEME ? '#e2e8f0' : '#334155',
          fontSize: '12px',
        },
      },
      series: questionAlignmentSeries,
    };

    const alignmentEvolutionOptions = {
      ...buildChartTheme({
        chart: {
          type: 'spline',
          scrollablePlotArea: {
            minWidth: 600,
            scrollPositionX: 1,
          },
        },
        title: 'Alignment Evolution',
        subtitle:
          totalParticipants === 0
            ? 'No participant records available for this session.'
            : 'Each band represents one individual moving across Q1-Q4 alignment buckets.',
        xAxisOptions: {
          minPadding: 0,
          maxPadding: 0,
          gridLineWidth: 1,
          gridLineColor: ENABLE_DARK_CHART_THEME ? '#1e293b' : '#999',
          gridLinePlacement: 'on',
        },
        categories: questionCategories,
        yAxisTitle: 'Alignment Level Buckets Per Question',
        series: alignmentAlluvialSeries,
        height: 580,
      }),
      legend: {
        enabled: true,
        itemStyle: {
          fontSize: '14px',
          fontWeight: '600',
          color: ENABLE_DARK_CHART_THEME ? '#e2e8f0' : '#334155',
        },
      },
      yAxis: {
        min: -0.5,
        max: 3.5,
        tickPositions: [-1, 0, 1, 2, 3, 4],
        allowDecimals: false,
        title: {
          text: 'Alignment Level',
          style: {
            fontSize: '14px',
            color: ENABLE_DARK_CHART_THEME ? '#cbd5e1' : '#334155',
          },
        },
        labels: {
          align: 'left',
          x: 30,
          style: {
            color: ENABLE_DARK_CHART_THEME ? '#cbd5e1' : '#334155',
            fontSize: '14px',
          },
          formatter() {
            const value = Number(this.value);
            if (value === 4) return '';
            if (value === 3) return 'High';
            if (value === 2) return 'Moderate-High';
            if (value === 1) return 'Partial';
            if (value === -1) return '';
            return 'Low';
          },
        },
        plotBands: [
          {
            from: -0.5,
            to: 0.5,
            color: ENABLE_DARK_CHART_THEME
              ? 'rgba(148, 163, 184, 0.06)'
              : 'rgba(61, 79, 66, 0.05)',
            borderColor: 'rgba(0,0,0,0.1)', // Border color
            borderWidth: 1, // Border thickness in pixels
          },
          {
            from: 0.5,
            to: 1.5,
            color: ENABLE_DARK_CHART_THEME
              ? 'rgba(30, 41, 59, 0.14)'
              : 'rgba(17, 161, 60, 0.10)',
            borderColor: 'rgba(0,0,0,0.1)', // Border color
            borderWidth: 1, // Border thickness in pixels
          },
          {
            from: 1.5,
            to: 2.5,
            color: ENABLE_DARK_CHART_THEME
              ? 'rgba(148, 163, 184, 0.06)'
              : 'rgba(17, 161, 60, 0.20)',
            borderColor: 'rgba(0,0,0,0.1)', // Border color
            borderWidth: 1, // Border thickness in pixels
          },
          {
            from: 2.5,
            to: 3.5,
            color: ENABLE_DARK_CHART_THEME
              ? 'rgba(30, 41, 59, 0.14)'
              : 'rgba(17, 161, 60, 0.30)',
            borderColor: 'rgba(0,0,0,0.1)', // Border color
            borderWidth: 1, // Border thickness in pixels
          },
        ],
        gridLineWidth: 0,
        gridLineColor: ENABLE_DARK_CHART_THEME ? '#1e293b' : '#e5e7eb',
      },
      tooltip: {
        shared: false,
        backgroundColor: ENABLE_DARK_CHART_THEME
          ? 'rgba(15, 23, 42, 0.96)'
          : 'rgba(255, 255, 255, 0.96)',
        borderColor: ENABLE_DARK_CHART_THEME ? '#334155' : '#cbd5e1',
        style: {
          color: ENABLE_DARK_CHART_THEME ? '#f8fafc' : '#0f172a',
        },
        formatter() {
          const scoreValue = this.point?.custom?.score;
          const scoreText = Number.isFinite(scoreValue)
            ? scoreValue.toFixed(2)
            : 'N/A';
          const decisionText = this.point?.custom?.decisionText;
          const decisionDisplay = decisionText
            ? escapeHtml(decisionText)
            : 'No decision recorded';

          // return `<b>Academic Profile</b>: ${this.series.name}<br/><b>Question</b>: ${this.x}<br/><b>Alignment</b>: ${this.point?.custom?.bucketLabel || 'N/A'}<br/><b>Decision</b>: ${decisionDisplay}`;
          return `<b>Academic Profile</b>: ${this.series.name}<br/><b>Question</b>: ${this.x}<br/><b>Alignment</b>: ${this.point?.custom?.bucketLabel || 'N/A'}<br/><b>[Click for details]</b>`;
        },
      },
      plotOptions: {
        spline: {
          lineWidth: 8,
          linecap: 'round',
          states: {
            hover: {
              lineWidth: 10,
            },
          },
          marker: {
            enabled: false,
          },
          point: {
            events: {
              click: function () {
                const pointCustom = this?.options?.custom || this?.custom;
                this.series?.chart?.options?.custom?.onParticipantPointClick(
                  pointCustom,
                );
              },
            },
          },
        },
      },
      custom: {
        onParticipantPointClick,
      },
    };

    const alignmentHistogramOptions = {
      ...buildChartTheme({
        title: 'Alignment Level By Academic Profile',
        subtitle:
          totalParticipants === 0
            ? 'No participant records available for this session.'
            : '',
        categories: agreementCategories,
        yAxisTitle: 'Amount of individuals',
        series: agreementSeries,
        height: 300,
      }),
    };

    return (
      <div>
        <Header />

        {selectedParticipantPath && (
          <>
            <div
              className='modal-backdrop fade show'
              onClick={this.closeParticipantPathModal}></div>
            <div
              className='modal fade show d-block'
              role='dialog'
              aria-modal='true'
              aria-labelledby='participant-path-title'
              onClose={this.closeParticipantPathModal}
              tabIndex='-1'>
              <div className='modal-dialog modal-xl modal-dialog-centered'>
                <div className='modal-content'>
                  <div className='modal-header'>
                    <h5 className='modal-title' id='participant-path-title'>
                      Individual Path ({selectedParticipantPath.profile})
                    </h5>
                    <button
                      type='button'
                      className='btn-close'
                      aria-label='Close'
                      onClick={this.closeParticipantPathModal}></button>
                  </div>
                  <div className='modal-body'>
                    <div className='table-responsive'>
                      <table className='table table-sm small table-striped align-middle'>
                        <thead>
                          <tr>
                            <th scope='col'>Question</th>
                            <th scope='col'>Alignment</th>
                            <th scope='col'>Decision</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedParticipantPath.path.map((step) => (
                            <tr
                              key={`${selectedParticipantPath.participantLabel}-${step.questionKey}`}>
                              <td className='py-3'>{step.questionLabel}</td>
                              <td className='py-3'>{step.alignment}</td>
                              <td className='py-3'>{step.decisionText}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className='modal-footer'>
                    <button
                      type='button'
                      className='btn btn-secondary'
                      onClick={this.closeParticipantPathModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <h2 className='d-flex mt-3 sec-title fs-3'>
          <span className='d-none d-xl-inline'>Summary</span>
          {/* Display sub-menu on larger viewports */}
          <DebriefMenu
            className='small d-flex justify-content-between justify-content-xl-end align-items-center ms-xl-auto w-100'
            game_id={gameData?._id}
            location={this.props.location}
          />
        </h2>

        <div className='row mt-3'>
          {/* <div className='col-12'>
            <p className='text-center mb-2'>
              Debrief view for sustainability teaching across academic levels.
              Compare alignment outcomes, intervention frequencies, and pre/post
              progression in one screen.
            </p>
          </div> */}

          {/* <div className='col-12'>
            <div className='row g-2 mb-2'>
              <div className='col-6 col-md-3'>
                <div className='border rounded p-2 h-100 bg-light'>
                  <small className='text-muted d-block'>Participants</small>
                  <strong className='fs-5'>{totalParticipants}</strong>
                </div>
              </div>
              <div className='col-6 col-md-3'>
                <div className='border rounded p-2 h-100 bg-light'>
                  <small className='text-muted d-block'>Completed</small>
                  <strong className='fs-5'>{completeParticipants}</strong>
                </div>
              </div>
              <div className='col-6 col-md-3'>
                <div className='border rounded p-2 h-100 bg-light'>
                  <small className='text-muted d-block'>Completion Rate</small>
                  <strong className='fs-5'>
                    {numeral(completionRate / 100).format('0.0%')}
                  </strong>
                </div>
              </div>
              <div className='col-6 col-md-3'>
                <div className='border rounded p-2 h-100 bg-light'>
                  <small className='text-muted d-block'>
                    Interventions Logged
                  </small>
                  <strong className='fs-5'>
                    {totalSubmittedInterventions}
                  </strong>
                </div>
              </div>
            </div>
          </div> */}

          <div className='col-12'>
            {/* <div className='row g-3 mb-1 highcharts-dark'>
              <div className='col-12'>
                <div className='border rounded p-2x'>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={questionAlignmentRadarOptions}
                  />
                </div>
              </div>
            </div> */}
            <div className='row g-3 mb-1'>
              {/* Chart: Alignment Evolution */}
              <div className='col-12 mb-5'>
                <div className='border rounded p-2'>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={alignmentEvolutionOptions}
                  />
                </div>
              </div>
              {/* Chart: Agreement Histogram */}
              {/* <div className='col-12 col-lg-6'>
                <div className='border rounded p-2'>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={alignmentHistogramOptions}
                  />
                </div>
              </div> */}
              {/* Chart: Cumulative Alignment Histogram */}
              {/* <div className='col-12 col-lg-6'>
                <div className='border rounded p-2'>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={cumulativeAlignmentHistogramOptions}
                  />
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { player, game } = state;
  return {
    game,
    player,
  };
}

export default connect(mapStateToProps)(DebriefSummary);
